import JSZip from 'jszip'
import type { EventConfig } from '@/types'
import { DEFAULT_SCHEMA_VERSION } from './constants'

/**
 * Event export/import.
 *
 * The whole EventConfig is serialised, so every setting on every editor page
 * travels by construction — there is no per-field allowlist to fall behind.
 *
 * Assets: anything in the config pointing at /uploads/ (or a blob: URL) is
 * bundled, plus the pose images referenced by capture.poseSuggestions, which
 * live in DATA_DIR/poses and are referenced by bare filename.
 *
 * Deliberately NOT bundled:
 *  - /captures/... — gallery photos are session data, not part of the event.
 *  - /props/, /sounds/, /carousel/, /default-assets/ — shipped with the app, so
 *    they resolve on any install.
 *  - /video/bg_video.mp4 — the attract-loop background is a ~230MB install-level
 *    asset kept out of git; the path is preserved and resolves wherever the file
 *    has been placed.
 */

/** Config file inside the zip. Older exports used `template.json`. */
const MANIFEST = 'event.json'
const LEGACY_MANIFEST = 'template.json'

export interface EventBundle {
  config: EventConfig
  /** zip path -> file blob, for assets referenced from the config. */
  assets: Record<string, Blob>
  assetMap: Record<string, string>
  /** Pose filename -> image blob, uploaded back under the same filename. */
  poses: Record<string, Blob>
}

/** Pose filenames referenced by this event's pose-suggestion settings. */
function posesOf(event: EventConfig): string[] {
  const poses = event.capture?.poseSuggestions?.poses
  if (!Array.isArray(poses)) return []
  return poses
    .map((p) => p?.filename)
    .filter((f): f is string => typeof f === 'string' && f.length > 0)
}

/**
 * Build the export zip. Split from the download so it can be exercised outside
 * a browser.
 */
export async function buildEventZip(
  event: EventConfig,
  getAssetBlob: (url: string) => Promise<Blob | null>
): Promise<Blob> {
  const zip = new JSZip()
  const assets = zip.folder('assets')
  const posesFolder = zip.folder('poses')
  const printLayouts = zip.folder('print-layouts')

  const assetMap: Record<string, string> = {}

  // Walks the entire config rather than named fields, so a new image setting on
  // any page is picked up without touching this code.
  const collectAssets = async (obj: unknown, path: string) => {
    if (!obj) return
    if (typeof obj === 'string' && (obj.startsWith('/uploads/') || obj.startsWith('blob:'))) {
      if (assetMap[obj]) return // already bundled — same image reused elsewhere
      const blob = await getAssetBlob(obj)
      if (blob) {
        // Keep the source extension where there is one; uploads are often
        // extensionless, in which case fall back to the blob's type.
        const fromUrl = obj.split('?')[0].split('.').pop() || ''
        const ext = /^[a-z0-9]{1,5}$/i.test(fromUrl) && obj.includes('.')
          ? fromUrl
          : blob.type.split('/')[1] || 'bin'
        const filename = `${path}-${Date.now()}.${ext}`
        assetMap[obj] = `assets/${filename}`
        // ArrayBuffer rather than the Blob itself: JSZip only recognises Blob
        // inputs in a browser, and this keeps the builder runnable under Node
        // so the export can be tested outside one.
        assets?.file(filename, await blob.arrayBuffer())
      }
    } else if (Array.isArray(obj)) {
      for (let i = 0; i < obj.length; i++) {
        await collectAssets(obj[i], `${path}-${i}`)
      }
    } else if (typeof obj === 'object') {
      for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
        await collectAssets(v, `${path}-${k}`)
      }
    }
  }

  await collectAssets(event, 'event')

  // Pose images are referenced by filename, not by URL, so they need collecting
  // separately or an imported event points at poses the install doesn't have.
  const bundledPoses: string[] = []
  for (const filename of posesOf(event)) {
    const blob = await getAssetBlob(`/poses/${filename}`)
    if (blob) {
      posesFolder?.file(filename, await blob.arrayBuffer())
      bundledPoses.push(filename)
    }
  }

  const configClone = JSON.parse(JSON.stringify(event))
  const replaceAssets = (obj: unknown): unknown => {
    if (!obj) return obj
    if (typeof obj === 'string' && assetMap[obj]) {
      return assetMap[obj]
    }
    if (Array.isArray(obj)) {
      return obj.map(replaceAssets)
    }
    if (typeof obj === 'object') {
      const out: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
        out[k] = replaceAssets(v)
      }
      return out
    }
    return obj
  }

  const exportedConfig = replaceAssets(configClone) as EventConfig

  zip.file(MANIFEST, JSON.stringify({
    schemaVersion: DEFAULT_SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    config: exportedConfig,
    assets: assetMap,
    poses: bundledPoses,
  }, null, 2))

  // Informational copy of the print layout, so the file is inspectable without
  // digging through the manifest. Import reads the layout from the config.
  if (event.printLayout) {
    printLayouts?.file(`${event.printLayout.id}.json`, JSON.stringify(event.printLayout, null, 2))
  }

  return zip.generateAsync({ type: 'blob' })
}

export async function exportEvent(
  event: EventConfig,
  getAssetBlob: (url: string) => Promise<Blob | null>
): Promise<void> {
  const blob = await buildEventZip(event, getAssetBlob)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `posebox-event-${event.name}-${new Date().toISOString().slice(0, 10)}.zip`
  a.click()
  // Revoking in the same tick can cancel a download that hasn't started yet on
  // some browsers, which is especially easy to hit with a large zip.
  setTimeout(() => URL.revokeObjectURL(url), 30000)
}

export async function importEventZip(file: File | Blob | ArrayBuffer): Promise<EventBundle | null> {
  try {
    // Normalise to ArrayBuffer: JSZip only accepts Blob/File inputs in a
    // browser, and this lets the importer run under Node for testing.
    const data = file instanceof ArrayBuffer ? file : await file.arrayBuffer()
    const zip = await JSZip.loadAsync(data)
    // Fall back to the old filename so zips exported before the rename import.
    const manifestJson =
      (await zip.file(MANIFEST)?.async('string')) ??
      (await zip.file(LEGACY_MANIFEST)?.async('string'))
    if (!manifestJson) return null
    const manifest = JSON.parse(manifestJson) as {
      schemaVersion: string
      config: EventConfig
      assets: Record<string, string>
      poses?: string[]
    }

    // Read as ArrayBuffer and wrap, rather than asking JSZip for a 'blob':
    // that output type is browser-only, and callers just need a Blob to upload.
    const assets: Record<string, Blob> = {}
    for (const zipPath of Object.values(manifest.assets || {})) {
      const buf = await zip.file(zipPath)?.async('arraybuffer')
      if (buf) assets[zipPath] = new Blob([buf])
    }

    // Trust the folder rather than the manifest list, so a zip that lost its
    // `poses` key (or an older export) still restores whatever is in there.
    const poses: Record<string, Blob> = {}
    const poseFiles = zip.folder('poses')
    if (poseFiles) {
      const entries: Array<{ name: string; file: JSZip.JSZipObject }> = []
      zip.forEach((relPath, entry) => {
        if (!entry.dir && relPath.startsWith('poses/')) {
          entries.push({ name: relPath.slice('poses/'.length), file: entry })
        }
      })
      for (const { name, file: entry } of entries) {
        if (!name) continue
        poses[name] = new Blob([await entry.async('arraybuffer')])
      }
    }

    return { config: manifest.config, assets, assetMap: manifest.assets || {}, poses }
  } catch (err) {
    // Logged rather than swallowed silently: a malformed zip used to fail as an
    // indistinguishable "nothing happened" in the UI.
    console.error('Event import failed:', err)
    return null
  }
}
